import { useState, useCallback, useRef, useEffect } from 'react'
import {
  AutocompleteSuggestion,
  ParsedAddress,
  AutocompleteResponse,
  PlaceDetailsResponse,
  AddressComponent,
} from '../components/AddressAutocomplete/types'

const DEBOUNCE_MS = 300
const GOOGLE_PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const GOOGLE_PLACES_DETAILS_URL = 'https://places.googleapis.com/v1/places'

/**
 * Generates a unique session token for Google Places API billing optimization
 */
function generateSessionToken(): string {
  return crypto.randomUUID()
}

/**
 * Parses address components from Google Places API response
 * Uses expanded city fallbacks: locality → postal_town → sublocality → administrative_area_level_2
 */
function parseAddressComponents(
  components: AddressComponent[],
): Omit<ParsedAddress, 'addressLine1'> {
  const getComponent = (types: string[]): AddressComponent | undefined => {
    for (const type of types) {
      const component = components.find(c => c.types.includes(type))
      if (component) return component
    }
    return undefined
  }

  // Get city with expanded fallbacks
  const cityComponent = getComponent([
    'locality',
    'postal_town',
    'sublocality',
    'administrative_area_level_2',
  ])

  const provinceComponent = getComponent(['administrative_area_level_1'])
  const postalCodeComponent = getComponent(['postal_code'])
  const countryComponent = getComponent(['country'])

  return {
    city: cityComponent?.longText ?? '',
    province: provinceComponent?.longText ?? '',
    postalCode: postalCodeComponent?.longText ?? '',
    // Use shortText (ISO-2) for country to match existing combobox values
    country: countryComponent?.shortText ?? '',
  }
}

/**
 * Builds addressLine1 from street_number and route components
 */
function buildAddressLine1(components: AddressComponent[]): string {
  const streetNumber = components.find(c => c.types.includes('street_number'))?.longText ?? ''
  const route = components.find(c => c.types.includes('route'))?.longText ?? ''

  if (streetNumber && route) {
    return `${streetNumber} ${route}`
  }
  return route || streetNumber
}

type UseGooglePlacesAutocompleteOptions = {
  apiKey?: string
  /** ISO-2 country code to filter suggestions (e.g., "CA", "US") */
  countryCode?: string
  onError?: (error: Error) => void
}

type UseGooglePlacesAutocompleteReturn = {
  suggestions: AutocompleteSuggestion[]
  isLoading: boolean
  error: Error | null
  searchAddress: (query: string) => void
  selectPlace: (placeId: string, fallbackText?: string) => Promise<ParsedAddress | null>
  clearSuggestions: () => void
  startSession: () => void
  endSession: () => void
}

/**
 * Hook for Google Places autocomplete functionality
 * Implements session tokens for billing optimization and debouncing for API efficiency
 */
export function useGooglePlacesAutocomplete(
  options: UseGooglePlacesAutocompleteOptions,
): UseGooglePlacesAutocompleteReturn {
  const { apiKey, countryCode, onError } = options

  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const sessionTokenRef = useRef<string | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Start a new session (generate new token)
  const startSession = useCallback(() => {
    sessionTokenRef.current = generateSessionToken()
  }, [])

  // End session (clear token)
  const endSession = useCallback(() => {
    sessionTokenRef.current = null
  }, [])

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const searchAddress = useCallback(
    (query: string) => {
      // Clear previous debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Clear suggestions if query is too short
      if (!query || query.length < 3) {
        setSuggestions([])
        return
      }

      // Skip if no API key
      if (!apiKey) {
        return
      }

      // Ensure we have a session token
      if (!sessionTokenRef.current) {
        startSession()
      }

      // Debounce the search
      debounceTimerRef.current = setTimeout(async () => {
        setIsLoading(true)
        setError(null)

        const controller = new AbortController()
        abortControllerRef.current = controller

        try {
          // Build request body with optional country filtering
          const requestBody: Record<string, unknown> = {
            input: query,
            sessionToken: sessionTokenRef.current,
          }

          // Add country filter if provided (uses ISO-2 country codes)
          if (countryCode) {
            requestBody.includedRegionCodes = [countryCode]
          }

          const response = await fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': apiKey,
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal,
          })

          if (!response.ok) {
            throw new Error(`Google Places API error: ${response.status}`)
          }

          const data = (await response.json()) as AutocompleteResponse

          const mappedSuggestions: AutocompleteSuggestion[] = (data.suggestions || [])
            .filter(suggestion => suggestion.placePrediction?.placeId)
            .map(suggestion => ({
              placeId: suggestion.placePrediction?.placeId ?? '',
              description: suggestion.placePrediction?.text?.text ?? '',
              mainText: suggestion.placePrediction?.structuredFormat?.mainText?.text ?? '',
              secondaryText:
                suggestion.placePrediction?.structuredFormat?.secondaryText?.text ?? '',
            }))

          setSuggestions(mappedSuggestions)
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            // Request was cancelled, ignore
            return
          }
          const error = err instanceof Error ? err : new Error('Unknown error')
          setError(error)
          onError?.(error)
        } finally {
          setIsLoading(false)
        }
      }, DEBOUNCE_MS)
    },
    [apiKey, countryCode, onError, startSession],
  )

  const selectPlace = useCallback(
    async (placeId: string, fallbackText?: string): Promise<ParsedAddress | null> => {
      if (!apiKey) {
        return null
      }

      setIsLoading(true)
      setError(null)

      try {
        const url = `${GOOGLE_PLACES_DETAILS_URL}/${placeId}`
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress',
          },
        })

        if (!response.ok) {
          throw new Error(`Google Places API error: ${response.status}`)
        }

        const data = (await response.json()) as PlaceDetailsResponse

        const addressComponents = data.addressComponents || []
        const parsed = parseAddressComponents(addressComponents)
        const addressLine1 = buildAddressLine1(addressComponents)

        // Use details-derived addressLine1, fallback to suggestion text if empty
        const finalAddressLine1 = addressLine1 || fallbackText || ''

        const result: ParsedAddress = {
          addressLine1: finalAddressLine1,
          ...parsed,
        }

        // Clear suggestions after selection
        setSuggestions([])

        // End session after place selection (per Google billing best practices)
        endSession()

        return result
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        onError?.(error)

        // Return partial address from suggestion text as fallback
        if (fallbackText) {
          return {
            addressLine1: fallbackText,
            city: '',
            province: '',
            postalCode: '',
            country: '',
          }
        }

        return null
      } finally {
        setIsLoading(false)
      }
    },
    [apiKey, onError, endSession],
  )

  return {
    suggestions,
    isLoading,
    error,
    searchAddress,
    selectPlace,
    clearSuggestions,
    startSession,
    endSession,
  }
}
